import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { useTranslation } from 'react-i18next';

const AddTagChoiceDialog = ({
  open,
  onClose,
  onManual,
  onAi,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0',
  manageTagsColor = '#6767c0',
}) => {
  const { t } = useTranslation();
  const addTagOptionSx = (accentColor) => ({
    display: 'grid',
    gridTemplateColumns: '56px 1fr',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    padding: '12px 16px',
    minHeight: '96px',
    borderRadius: '16px',
    border: `1px solid ${accentColor}30`,
    background: `linear-gradient(135deg, #ffffff 0%, ${accentColor}12 100%)`,
    boxShadow: '0 3px 14px rgba(0,0,0,0.08)',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: accentColor,
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 20px ${accentColor}26`,
    },
    '&:active': {
      transform: 'translateY(-1px)',
    },
    '&:focus-visible': {
      outline: `2px solid ${accentColor}55`,
      outlineOffset: '2px',
    },
  });
  const addTagOptionIconSx = (accentColor) => ({
    width: 44,
    height: 44,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${accentColor}24 0%, ${accentColor}14 100%)`,
    color: accentColor,
    boxShadow: `inset 0 0 0 1px ${accentColor}24`,
    flexShrink: 0,
    alignSelf: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: '22px',
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '14px',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${hoverColor} 100%)`,
          color: 'white',
          pt: 4.5,
          pb: 3.5,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            <AddRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, letterSpacing: '0.2px' }}>
              {t('add_tag_dialog.select_tag_type') || 'Add tag'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {t('add_tag_dialog.select_tag_type_description') || 'Choose how you want to add a tag for this event.'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <DialogContent sx={{ pt: 4.5, pb: 3, px: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Paper
            component={ButtonBase}
            disableRipple
            onClick={onManual}
            sx={addTagOptionSx(primaryColor)}
            aria-label={t('add_tag_dialog.manual_tag') || 'Manual tag'}
          >
            <Box sx={addTagOptionIconSx(primaryColor)}>
              <EditNoteRoundedIcon />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#2f2f2f' }}>
                {t('add_tag_dialog.manual_tag') || 'Manual tag'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5f5f5f' }}>
                {t('add_tag_dialog.manual_tag_description') || 'Create a one-off tag for this event.'}
              </Typography>
            </Box>
          </Paper>
          <Paper
            component={ButtonBase}
            disableRipple
            onClick={onAi}
            sx={addTagOptionSx(manageTagsColor)}
            aria-label={t('add_tag_dialog.ai_tag') || 'AI tag (smart tags list)'}
          >
            <Box sx={addTagOptionIconSx(manageTagsColor)}>
              <AutoAwesomeRoundedIcon />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#2f2f2f' }}>
                {t('add_tag_dialog.ai_tag') || 'AI tag (smart tags list)'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5f5f5f' }}>
                {t('add_tag_dialog.ai_tag_description') || 'Add or manage smart tags for AI tagging.'}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', fontWeight: 600, color: primaryColor }}
        >
          {t('add_tag_dialog.cancel') || 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTagChoiceDialog;
